import { z } from 'zod'

/**
 * `LGH-039`. Wire shape transcribed from `REQ-lighthouse-price-list-upload-
 * and-versioning-shape`'s own answer — live-verified there
 * (`scripts/verify-tenant-price-lists-live.ts`, 21 checks), not a contract
 * file this repo can read (`customer-app` isn't in Lighthouse's whitelist).
 *
 * `supplier`/`currency` were not in the original proposal — `1C` added them
 * as required, no default, because `ingestPriceListFile`'s own
 * `PriceListDocumentMeta` requires both (`R-079`: a price list in the wrong
 * currency read silently is this domain's worst failure). If the upload form
 * doesn't already collect both, that's a real gap, not something this schema
 * can paper over with a default.
 *
 * `acceptedCount`/`refusedCount` are what the ingestion actually did —
 * without them here, that result is lost the moment the response closes.
 */
export const priceListUploadStatusSchema = z.enum(['active', 'superseded'])

export const priceListUploadRecordSchema = z
  .object({
    id: z.string(),
    effectiveDate: z.string(),
    uploadedAt: z.string(),
    uploadedByUserId: z.string(),
    fileName: z.string(),
    sizeBytes: z.number(),
    status: priceListUploadStatusSchema,
    supplier: z.string(),
    currency: z.string(),
    acceptedCount: z.number(),
    refusedCount: z.number(),
  })
  .strict()
export type PriceListUploadRecord = z.infer<typeof priceListUploadRecordSchema>

export const priceListHistorySchema = z.array(priceListUploadRecordSchema)
export type PriceListHistory = z.infer<typeof priceListHistorySchema>

/**
 * `POST`'s own response — the just-created record, plus which rows (if any)
 * `ingestPriceListFile` refused and why, so the admin sees the outcome
 * immediately rather than having to guess from `refusedCount` alone.
 */
export const priceListRefusedRowSchema = z
  .object({
    rowId: z.string(),
    reason: z.string(),
  })
  .strict()

export const priceListUploadResponseSchema = z
  .object({
    upload: priceListUploadRecordSchema,
    refused: z.array(priceListRefusedRowSchema).optional(),
  })
  .strict()
export type PriceListUploadResponse = z.infer<typeof priceListUploadResponseSchema>
